import api from "../../../BrandOnBoarding/api";
import { OVERVIEW_LLM_LABELS } from "../../../utils/constant";

const ORCHESTRATOR_BASE_URL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;

/**
 * Raw LLM keys ("chatgpt_bd", "gpt-5.6") → the same friendly display labels
 * OVERVIEW_LLM_LABELS already provides everywhere else in the app — Home
 * Page was showing the raw key verbatim, unlike every other module.
 */
export function mapLlmLabels(values) {
  const list = Array.isArray(values) ? values : values ? [values] : [];
  return list.map((v) => OVERVIEW_LLM_LABELS[v] || v);
}

export function llmLabel(value) {
  return OVERVIEW_LLM_LABELS[value] || value || "";
}

/**
 * GET /orchestrator/getHomePageData?userId=&industry_customer_id=&modules=&page=&limit=
 *
 * One module per call — `modules` is required and single-valued, and the
 * response is flat (no `modules.<key>` nesting):
 * { success, generatedAt, totalCount, failedCount, page, limit, hasNextPage, records: [...] }
 *
 * Each tab fetches only its own module, so switching tabs never re-computes
 * the others.
 */
export async function fetchModulePage({ userId, customerId, moduleKey, page = 1, limit = 5 }) {
  const response = await api.get(`${ORCHESTRATOR_BASE_URL}/getHomePageData`, {
    params: {
      userId,
      industry_customer_id: customerId,
      modules: moduleKey,
      page,
      limit,
    },
  });
  return response?.data;
}

/**
 * GET /orchestrator/getHomePageData/search/:moduleKey/:customerId/:userId
 * NOTE the reversed path-param order vs. the two calls above — customerId
 * comes before userId here. Get this backwards and you silently query the
 * wrong scope rather than erroring, so don't "fix" it to match the other calls.
 * Response envelope is different too: { data: [...], totalCount, totalPages, currentPage, pageSize }
 * — no success/generatedAt wrapper, no nested modules.<key>.
 */
export async function searchModule({ moduleKey, customerId, userId, search, page, limit }) {
  const response = await api.get(
    `${ORCHESTRATOR_BASE_URL}/getHomePageData/search/${moduleKey}/${customerId}/${userId}`,
    { params: { search, page, limit } }
  );
  return response?.data;
}

/**
 * V2 of fetchModulePage for the merged "Brand Assessment" tab.
 * GET /orchestrator/getHomePageDataV2?userId=&industry_customer_id=&page=&limit=&includeCategory=
 *
 * DIFFERENT FROM V1: no `modules` param (always the merged list), and the
 * list holds Brand/Product Assessment runs AND Category Analysis runs
 * (records carry `recordKind` + `type: "Category"` for the latter).
 * `includeCategory: false` leaves Category runs out (user without the
 * Category Analysis flag). Response shape is identical to V1.
 * To revert, stop using this (see moduleRegistry.js) — V1 is untouched.
 */
export async function fetchModulePageV2({ userId, customerId, page = 1, limit = 5, includeCategory = true }) {
  const response = await api.get(`${ORCHESTRATOR_BASE_URL}/getHomePageDataV2`, {
    params: { userId, industry_customer_id: customerId, page, limit, includeCategory },
  });
  return response?.data;
}

/**
 * V2 of searchModule — GET /orchestrator/getHomePageDataV2/search/:customerId/:userId
 * Same reversed customerId-before-userId order as V1 search, but no moduleKey
 * segment. Same { data, totalCount, ... } response envelope as V1.
 */
export async function searchModuleV2({ customerId, userId, search, page, limit, includeCategory = true }) {
  const response = await api.get(`${ORCHESTRATOR_BASE_URL}/getHomePageDataV2/search/${customerId}/${userId}`, {
    params: { search, page, limit, includeCategory },
  });
  return response?.data;
}

/**
 * Retry a failed transaction. Reuses the same endpoint DiscoveryV2/Discovery.js's
 * own retryDiscovery() already calls in production.
 */
export async function retryTransaction(transactionId) {
  const response = await api.get(`${ORCHESTRATOR_BASE_URL}/retry-transaction-request/${transactionId}`);
  return response?.data;
}

/**
 * GET /api/llm-reporting/html-history?userId=&transactionId=
 * Called on-demand, only when the "N more reports" modal opens (never on
 * page load), per record.
 *
 * Response shape confirmed by inspection of the same endpoint's existing use
 * in Dashboard/HTMLReporting/useMonitoringHTMLReportHistory.js (queried
 * there by `types` instead of `transactionId`, but the same row shape):
 * raw rows are { id, status, selections, start_date, transaction_id }, not
 * the { id, type, status, generatedAt, downloadUrl } shape the drawer spec
 * guessed — normalized here to what ReportsModal actually renders.
 */
// Category Analysis's own report-type vocabulary — same labels/keys as
// BrandAudit/HTMLReportTab/HTMLReportHistoryItem.js's REPORT_TYPE_LABELS
// (that real Reporting tab is the source of truth for these names).
const CATEGORY_REPORT_TYPE_LABELS = {
  categoryTeaser: "Category Teaser",
  categoryBrief: "Category Brief",
  categoryAnalysis: "Category Analysis Report",
};

/**
 * Friendly "Type" badge text for a report row — module-aware, because each
 * module's own real Reporting tab has its own vocabulary for the same raw
 * BE fields:
 *   - Brand Audit: `type`/`reportType` is a raw job-type string that either
 *     contains "comprehensive" (a full Report) or doesn't (a Presentation)
 *     — same check HTMLReportHistoryItem.js's own `isComprehensive` uses.
 *   - Category Analysis: `selections.reportType` is one of
 *     categoryTeaser/categoryBrief/categoryAnalysis — same field/labels
 *     that module's own Reporting tab shows.
 *   - Everything else (Discovery, ...): no distinct type vocabulary: only
 *     used where a caller opts in via showType, so this just falls back to
 *     whatever raw type string is present, same as before.
 * `row` may be either a live html-history row ({ type, selections }) or the
 * flatter `report.latest` shape off the main record ({ type }, no
 * `selections`) — both are handled.
 */
export function reportTypeLabel(moduleKey, row) {
  const rawType = row?.type ?? row?.input_summary?.type ?? null;
  if (moduleKey === "brandAudit") {
    return rawType && String(rawType).includes("comprehensive") ? "Report" : "Presentation";
  }
  if (moduleKey === "categoryAnalysis") {
    const reportType = row?.selections?.reportType ?? row?.reportType ?? row?.input_summary?.reportType ?? null;
    return CATEGORY_REPORT_TYPE_LABELS[reportType] || CATEGORY_REPORT_TYPE_LABELS.categoryAnalysis;
  }
  return rawType;
}

/**
 * Which transactionId to deep-link into on Brand Audit / Category
 * Analysis's real page. A record's own transactionId is its FINAL run —
 * but a Final run that hasn't started yet (status INITIATED) isn't in the
 * record's testIds, and those pages pick which tab to show by finding the
 * opened id inside testIds, so opening the Final id lands on no tab at
 * all. In that case open the latest non-inactive Test run instead — the
 * same thing that page's own History list does on click
 * (History/HistoryV2.js getTransactionData). When the record's own id IS
 * in testIds (Final completed) it's returned unchanged.
 */
export function resolveOpenTransactionId(record) {
  const tests = (record?.testIds || []).filter((t) => t?.status !== "INACTIVE");
  if (tests.length === 0) return record?.transactionId;
  if (tests.some((t) => String(t.transactionId) === String(record.transactionId))) return record.transactionId;
  return tests[tests.length - 1].transactionId;
}

export async function fetchReportHistory({ userId, transactionId, moduleKey }) {
  const response = await api.get("/llm-reporting/html-history", {
    params: { userId, transactionId },
  });
  const rows = Array.isArray(response?.data) ? response.data : [];
  // Same cancelled-row filter useMonitoringHTMLReportHistory.js's own
  // fetchJobsFromAPI() applies to this identical endpoint's response.
  return rows
    .filter((row) => row.status !== "cancelled")
    .map((row) => ({
      jobId: row.id,
      status: row.status,
      type: reportTypeLabel(moduleKey, row),
      category: row?.selections?.category || "",
      llms: row?.selections?.llms || [],
      markets: row?.selections?.markets || [],
      persona: row?.selections?.persona || [],
      date: row.start_date,
    }));
}

/**
 * Deletes/cancels one report — same real endpoint
 * DiscoveryV2/Reporting/DiscoveryReportingModal.js's own delete action uses
 * (there is no separate delete endpoint; cancelling a completed report and
 * cancelling an in-progress one are the same call).
 */
export async function cancelReport(jobId) {
  const response = await api.get(`/llm-reporting/cancel/${jobId}`);
  return response?.data;
}

/**
 * Same download endpoint already used by
 * Dashboard/HTMLReporting/useMonitoringHTMLReportHistory.js's downloadReport().
 */
export function reportDownloadUrl(jobId) {
  return `/llm-reporting/download/${jobId}`;
}

/**
 * Fetches one report's raw HTML for inline preview — same endpoint as
 * reportDownloadUrl()/downloadReport(), but as text (not a blob) so it can
 * go straight into an iframe's srcDoc. Same pattern
 * BrandAudit/HTMLReportTab.js's handleViewInline() already uses for its own
 * "View" action, kept generic here so any module's report list can reuse it.
 */
export async function fetchReportHtml(jobId) {
  const response = await api.get(`/llm-reporting/download/${jobId}`, { responseType: "text" });
  return response?.data || "";
}

/**
 * Opens a report as a real blob URL in a new tab — shared by every "Open in
 * new tab" action across the report row/table components.
 */
export async function openReportInNewTab(jobId) {
  const response = await api.get(reportDownloadUrl(jobId), { responseType: "blob" });
  const blob = new Blob([response.data], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/**
 * category/market/llm now come back as arrays from getHomePageData (a
 * transaction can run multiple of each — "multi-combination"), same shape
 * the search endpoint already used. Anywhere that only needs ONE display
 * value (a subtitle, a meta line) should read through this rather than
 * assume a plain string — anywhere that needs the full set (overflow
 * pills/tooltips) should use the array directly.
 */
export function firstOf(value) {
  if (Array.isArray(value)) return value[0] ?? "";
  return value ?? "";
}

const sameValue = (a, b) => String(a ?? "").toLowerCase().trim() === String(b ?? "").toLowerCase().trim();

/**
 * A record's scores/insights now live under `metrics[]`, one entry per
 * combination of {category, llm, market, persona} — Discovery runs a single
 * combination (one entry), but modules like Brand Audit run several, so the
 * caller may pass the combination it wants; without one, the first entry is
 * used.
 *
 * Returns { scores, insights, combination } — always the same shape, so no
 * component needs to know whether it's reading the current nested response
 * or the older flat one (the search endpoint still returns scores at the
 * record's top level until it's updated too).
 */
export function resolveMetrics(record, combination) {
  const entries = Array.isArray(record?.metrics) ? record.metrics : [];

  if (entries.length === 0) {
    return { scores: normalizeScoreKeys(record?.scores), insights: [], combination: null };
  }

  const entry = combination ? entries.find((e) => matchesCombination(e?.combination, combination)) || entries[0] : entries[0];

  return {
    scores: normalizeScoreKeys(entry?.scores),
    insights: Array.isArray(entry?.insights) ? entry.insights : [],
    combination: entry?.combination || null,
  };
}

/**
 * Matches on whichever combination fields the caller actually specifies —
 * Brand Audit selects by category/market/persona only (LLM is shown as a
 * table of every matching entry instead), so an unspecified field must not
 * disqualify an entry.
 */
export function matchesCombination(entryCombination, wanted) {
  if (!entryCombination || !wanted) return false;
  return ["category", "market", "persona", "llm"].every(
    (key) => wanted[key] == null || sameValue(entryCombination[key], wanted[key])
  );
}

/**
 * Brand Audit product runs report the pillars slot as `product_dimension`
 * instead of `brand_pillars_score`. Both mean the same 0-10 metric in the
 * same 15%-weighted slot, so they're collapsed to one key here rather than
 * making every component check for two.
 */
export function normalizeScoreKeys(scores) {
  if (!scores) return null;
  if (scores.brand_pillars_score != null || !("product_dimension" in scores)) return scores;
  const { product_dimension, ...rest } = scores;
  return { ...rest, brand_pillars_score: product_dimension };
}

/**
 * Category Analysis's `metrics[]` entries aren't the single-brand
 * {scores, insights} shape resolveMetrics() reads — a transaction has no
 * main brand, so each entry is a whole competitive leaderboard instead:
 * { kind: "leaderboard" | "leaderboard_composite", combination, leaderboard: [{brand, scores}] }.
 * "leaderboard" is one real LLM's run; "leaderboard_composite" (llm: "ALL")
 * is BE's pre-merged average across every LLM in that category/market/
 * persona, only present once 2+ LLMs ran it.
 *
 * findLeaderboardEntry — the one entry (if any) matching `kind` + combination.
 */
export function findLeaderboardEntry(record, combination, kind = "leaderboard") {
  const entries = Array.isArray(record?.metrics) ? record.metrics : [];
  return entries.find((e) => e?.kind === kind && matchesCombination(e?.combination, combination)) || null;
}

/**
 * findAllLeaderboardEntries — every entry matching `kind` + combination
 * (typically combination omits `llm` here, so this returns one entry per
 * LLM that ran that category/market/persona) — used to build a per-LLM
 * breakdown for a single brand regardless of which view mode is active.
 */
export function findAllLeaderboardEntries(record, combination, kind = "leaderboard") {
  const entries = Array.isArray(record?.metrics) ? record.metrics : [];
  return entries.filter((e) => e?.kind === kind && matchesCombination(e?.combination, combination));
}

/**
 * Normalizes status casing once, at the API boundary — live data returns
 * uppercase ("COMPLETED"), never compare raw status strings in components.
 * Mirrors the real per-module History lists' own status map (BrandAudit's
 * HistoryV2.js and CategoryAnalysis's CAHistory.js both use this exact
 * mapping) — RETRY reads as failed (it needs a retry, same as an outright
 * failure), not as a neutral/test state.
 */
export function pillClass(status) {
  const s = String(status || "").toUpperCase();
  if (s === "FINAL") return "final";
  if (s === "COMPLETED") return "completed";
  if (s === "RETRY" || s === "FAILED") return "failed";
  if (s === "RUNNING" || s === "PENDING" || s === "INITIATED") return "inProgress";
  return "test";
}

const STATUS_LABELS = {
  COMPLETED: "Completed",
  RUNNING: "In Progress",
  PENDING: "In Progress",
  INITIATED: "In Progress",
  RETRY: "Failed",
  FAILED: "Failed",
};

/**
 * Human-readable status text for a card/pill — same labels the real History
 * lists show, rather than the raw backend status string ("RETRY", "RUNNING")
 * verbatim.
 */
export function statusLabel(status) {
  const s = String(status || "").toUpperCase();
  return STATUS_LABELS[s] || status || "";
}

/**
 * Test vs Final run label — same field (`audit_type`, falling back to
 * `run_type`) and same case-insensitive "test" check the real BrandAudit/
 * CategoryAnalysis History lists use.
 */
export function auditTypeLabel(auditType) {
  return String(auditType || "").toLowerCase() === "test" ? "Test" : "Final";
}
